import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import OpenAIService from '../../../services/openaiService';

const AIRecommendations = ({ portfolioData, trends, collections, topPerformers }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    if (portfolioData && user) {
      generateRecommendations();
    }
  }, [portfolioData, user]);

  const generateRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await OpenAIService?.generateRecommendations(
        portfolioData,
        trends,
        collections,
        topPerformers
      );

      if (result?.error) {
        throw new Error(result?.error);
      }

      setRecommendations(result?.data);
    } catch (err) {
      setError(err?.message || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const getTypeStyle = (type) => {
    const styles = {
      opportunity: { icon: 'TrendingUp', color: 'text-success', bg: 'bg-success/10', border: 'border-success/20' },
      warning: { icon: 'AlertTriangle', color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20' },
      info: { icon: 'Info', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
      alert: { icon: 'AlertCircle', color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/20' }
    };
    return styles?.[type] || styles?.info;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: { label: 'High Priority', color: 'bg-destructive/20 text-destructive' },
      medium: { label: 'Medium', color: 'bg-warning/20 text-warning' },
      low: { label: 'Low', color: 'bg-muted text-muted-foreground' }
    };
    return badges?.[priority] || badges?.medium;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 mb-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Sparkles" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">AI-Powered Recommendations</h3>
            <p className="text-sm text-muted-foreground">Analyzing your portfolio...</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-muted/30 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-destructive" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">AI Recommendations</h3>
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
        <Button onClick={generateRecommendations} iconName="RefreshCw" variant="outline">
          Retry Analysis
        </Button>
      </div>
    );
  }

  if (!recommendations) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-warning rounded-lg flex items-center justify-center">
            <Icon name="Sparkles" size={20} className="text-background" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">AI-Powered Recommendations</h3>
            <p className="text-sm text-muted-foreground">Personalized insights based on your portfolio</p>
          </div>
        </div>
        <Button onClick={generateRecommendations} loading={loading} iconName="RefreshCw" variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      {/* Portfolio Health Score */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Portfolio Health</span>
            <Icon name="Activity" size={16} className="text-primary" />
          </div>
          <div className="text-2xl font-bold text-card-foreground">
            {recommendations?.overallScore}/100
          </div>
          <div className="w-full bg-muted rounded-full h-2 mt-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${recommendations?.overallScore}%` }}
            />
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Diversification</span>
            <Icon name="PieChart" size={16} className="text-conversion-accent" />
          </div>
          <div className="text-2xl font-bold text-card-foreground">
            {recommendations?.keyMetrics?.diversificationScore}/10
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {recommendations?.keyMetrics?.diversificationScore >= 7 ? 'Well diversified' : 'Needs improvement'}
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Risk Level</span>
            <Icon name="Shield" size={16} className="text-warning" />
          </div>
          <div className="text-2xl font-bold text-card-foreground capitalize">
            {recommendations?.keyMetrics?.riskLevel}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Growth: {recommendations?.keyMetrics?.growthPotential}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-card-foreground">{recommendations?.summary}</p>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="space-y-3">
        {recommendations?.recommendations?.map((rec, index) => {
          const style = getTypeStyle(rec?.type);
          const badge = getPriorityBadge(rec?.priority);
          const isExpanded = expandedIndex === index;

          return (
            <div
              key={index}
              className={`border rounded-lg transition-all duration-300 cursor-pointer premium-hover ${
                isExpanded
                  ? `${style?.bg} ${style?.border}`
                  : 'bg-muted/20 border-border hover:bg-muted/40'
              }`}
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
            >
              <div className="p-4">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${style?.bg}`}>
                    <Icon name={style?.icon} size={20} className={style?.color} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-card-foreground">
                        {rec?.title}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge?.color}`}>
                          {badge?.label}
                        </span>
                        <Icon 
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                          className="text-muted-foreground"
                        />
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {rec?.description}
                    </p>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-border space-y-3">
                        <div className="flex items-start space-x-2">
                          <Icon name="Target" size={16} className={style?.color} />
                          <div>
                            <div className="text-xs font-medium text-card-foreground mb-1">Recommended Action</div>
                            <div className="text-sm text-muted-foreground">{rec?.action}</div>
                          </div>
                        </div>
                        
                        {rec?.expectedImpact && (
                          <div className="flex items-start space-x-2">
                            <Icon name="TrendingUp" size={16} className={style?.color} />
                            <div>
                              <div className="text-xs font-medium text-card-foreground mb-1">Expected Impact</div>
                              <div className="text-sm text-muted-foreground">{rec?.expectedImpact}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Sparkles" size={14} />
            <span>Powered by OpenAI GPT-5</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {recommendations?.recommendations?.length} recommendations
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIRecommendations;
